// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/state-machines/connectivity-machine.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/state-machines/src/connectivity-machine.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo field-check-app
//     node fleet/fleet.ts check --repo field-check-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { assign, setup } from "xstate";
import type { MaintenanceScope } from "@fleet/maintenance";

// Exponential backoff limits
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const BACKOFF_BASE = 2;

// --- Types ---

export type SyncStatus = "connected" | "connecting" | "disconnected" | "error";
export type ApiStatus = "up" | "down" | "checking" | "maintenance";

export interface ConnectivityContext {
  retryCount: number;
  apiStatus: ApiStatus;
  syncStatus: SyncStatus;
  isOnline: boolean;
  isAuthenticated: boolean;
  lastError: string | null;
  serverVersion: string | null;
  maintenanceMessage: string | null;
  /**
   * Scopes of the CURRENT maintenance window (empty when there is none).
   *
   * Separate from the `maintenance` state on purpose: a `writes`-only window
   * arrives with `apiStatus: "up"` — the server keeps `status` at `"ok"` so the
   * machine never enters the blocking `maintenance` state for a pause the user
   * should barely notice. The scope is what lets the UI stay proportionate.
   */
  maintenanceScopes: MaintenanceScope[];
  isUploading: boolean;
  uploadQueueSize: number;
}

export type ConnectivityEvent =
  | { type: "NETWORK_CHANGED"; isOnline: boolean }
  | { type: "AUTH_CHANGED"; isAuthenticated: boolean }
  | { type: "POWERSYNC_STATUS_CHANGED"; status: SyncStatus }
  | {
      type: "API_HEALTH_CHANGED";
      status: "up" | "down" | "maintenance";
      version?: string;
      message?: string;
      maintenanceScopes?: MaintenanceScope[];
    }
  | { type: "RETRY" }
  | { type: "SYNC_STATUS_CHANGED"; isUploading: boolean; uploadQueueSize: number };

// --- Machine Logic ---

export const connectivityMachine = setup({
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: ConnectivityContext;
    events: ConnectivityEvent;
  },
  actions: {
    setSyncStatus: assign(({ event }) => {
      if (event.type === "SYNC_STATUS_CHANGED") {
        return {
          isUploading: event.isUploading,
          uploadQueueSize: event.uploadQueueSize,
        };
      }
      return {};
    }),
    setNetwork: assign({
      isOnline: ({ context, event }) =>
        event.type === "NETWORK_CHANGED" ? event.isOnline : context.isOnline,
    }),
    setAuth: assign({
      isAuthenticated: ({ context, event }) => {
        if (event.type === "AUTH_CHANGED") {
          return event.isAuthenticated;
        }
        return context.isAuthenticated;
      },
    }),
    setSync: assign({
      syncStatus: ({ context, event }) => {
        if (event.type === "POWERSYNC_STATUS_CHANGED") {
          return event.status;
        }
        return context.syncStatus;
      },
    }),
    setApi: assign({
      apiStatus: ({ context, event }) => {
        if (event.type === "API_HEALTH_CHANGED") {
          return event.status;
        }
        return context.apiStatus;
      },
      serverVersion: ({ context, event }) => {
        if (event.type === "API_HEALTH_CHANGED" && event.version) {
          return event.version;
        }
        return context.serverVersion;
      },
      maintenanceMessage: ({ context, event }) => {
        if (event.type !== "API_HEALTH_CHANGED") {
          return context.maintenanceMessage;
        }
        if (event.message) {
          return event.message;
        }
        // A window that CLOSED (or a silent one) must clear the previous notice.
        // Keeping the old string here is what left "We're back at 10:00" on
        // screen after the window had already lifted.
        return event.maintenanceScopes && event.maintenanceScopes.length > 0
          ? context.maintenanceMessage
          : null;
      },
      maintenanceScopes: ({ context, event }) => {
        if (event.type === "API_HEALTH_CHANGED") {
          return event.maintenanceScopes ?? [];
        }
        return context.maintenanceScopes;
      },
    }),
    incrementRetry: assign({
      retryCount: ({ context }) => context.retryCount + 1,
    }),
    resetRetry: assign({
      retryCount: 0,
      lastError: null,
    }),
  },
  guards: {
    isOffline: ({ context }) => !context.isOnline,
    isUnauthenticated: ({ context }) => !context.isAuthenticated,
    isFullyConnected: ({ context }) =>
      context.isOnline &&
      context.isAuthenticated &&
      context.apiStatus === "up" &&
      context.syncStatus === "connected",
    hasConnectionErrors: ({ context }) =>
      context.isOnline &&
      context.isAuthenticated &&
      (context.apiStatus === "down" || context.syncStatus === "error"),
    isMaintenance: ({ context }) => context.apiStatus === "maintenance",

    isEventOnline: ({ event }) => event.type === "NETWORK_CHANGED" && event.isOnline,
    isEventOffline: ({ event }) => event.type === "NETWORK_CHANGED" && !event.isOnline,
    isEventAuthenticated: ({ event }) => event.type === "AUTH_CHANGED" && event.isAuthenticated,
    isEventUnauthenticated: ({ event }) => event.type === "AUTH_CHANGED" && !event.isAuthenticated,
    isEventApiUp: ({ event }) => event.type === "API_HEALTH_CHANGED" && event.status === "up",
    isEventApiDown: ({ event }) => event.type === "API_HEALTH_CHANGED" && event.status === "down",
    isEventApiMaintenance: ({ event }) =>
      event.type === "API_HEALTH_CHANGED" && event.status === "maintenance",
    isEventSyncError: ({ event }) =>
      event.type === "POWERSYNC_STATUS_CHANGED" && event.status === "error",
    isEventSyncConnected: ({ event }) =>
      event.type === "POWERSYNC_STATUS_CHANGED" && event.status === "connected",
    isEventSyncNotConnected: ({ event }) =>
      event.type === "POWERSYNC_STATUS_CHANGED" && event.status !== "connected",
  },
  delays: {
    BACKOFF_DELAY: ({ context }) => {
      // Exponential backoff: base * BACKOFF_BASE^retryCount + jitter
      const delay = Math.min(BASE_DELAY_MS * BACKOFF_BASE ** context.retryCount, MAX_DELAY_MS);
      const JITTER_MAX_MS = 1000;
      const jitter = Math.random() * JITTER_MAX_MS;
      return delay + jitter;
    },
  },
}).createMachine({
  id: "connectivity",
  initial: "initializing",
  context: {
    isOnline: true,
    isAuthenticated: false,
    apiStatus: "up",
    isUploading: false,
    uploadQueueSize: 0,
    retryCount: 0,
    syncStatus: "disconnected",
    lastError: null,
    serverVersion: null,
    maintenanceMessage: null,
    maintenanceScopes: [],
  },
  on: {
    SYNC_STATUS_CHANGED: {
      actions: "setSyncStatus",
    },
    NETWORK_CHANGED: {
      actions: "setNetwork",
      target: ".checking",
    },
    AUTH_CHANGED: { actions: "setAuth" },
    POWERSYNC_STATUS_CHANGED: { actions: "setSync" },
    API_HEALTH_CHANGED: { actions: "setApi" },
  },
  states: {
    initializing: {
      always: [
        { target: "offline", guard: "isOffline" },
        { target: "maintenance", guard: "isMaintenance" },
        { target: "unauthenticated", guard: "isUnauthenticated" },
        { target: "checking" },
      ],
    },
    offline: {
      on: {
        RETRY: { target: "manualChecking" },
        NETWORK_CHANGED: [
          { target: "checking", guard: "isEventOnline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
      },
    },
    unauthenticated: {
      on: {
        RETRY: { target: "manualChecking" },
        AUTH_CHANGED: [
          { target: "checking", guard: "isEventAuthenticated", actions: "setAuth" },
          { actions: "setAuth" },
        ],
        NETWORK_CHANGED: [
          { target: "offline", guard: "isEventOffline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
      },
    },
    checking: {
      always: [
        { target: "offline", guard: "isOffline" },
        { target: "maintenance", guard: "isMaintenance" },
        { target: "unauthenticated", guard: "isUnauthenticated" },
        { target: "connected", guard: "isFullyConnected" },
        { target: "reconnecting", guard: "hasConnectionErrors" },
      ],
      after: {
        // Circuit breaker: prevent getting stuck on "Checking"
        // PowerSync sometimes takes a while on first connect or after resume
        20_000: "reconnecting",
      },
      on: {
        API_HEALTH_CHANGED: [
          { target: "maintenance", guard: "isEventApiMaintenance", actions: "setApi" },
          { target: "reconnecting", guard: "isEventApiDown", actions: "setApi" },
          { target: "checking", actions: "setApi" },
        ],
        POWERSYNC_STATUS_CHANGED: [
          { target: "reconnecting", guard: "isEventSyncError", actions: "setSync" },
          { target: "checking", actions: "setSync" },
        ],
        NETWORK_CHANGED: [
          { target: "offline", guard: "isEventOffline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
        AUTH_CHANGED: [
          { target: "unauthenticated", guard: "isEventUnauthenticated", actions: "setAuth" },
          { actions: "setAuth" },
        ],
      },
    },
    connected: {
      entry: "resetRetry",
      on: {
        RETRY: { target: "manualChecking" },
        NETWORK_CHANGED: [
          { target: "offline", guard: "isEventOffline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
        AUTH_CHANGED: [
          { target: "unauthenticated", guard: "isEventUnauthenticated", actions: "setAuth" },
          { actions: "setAuth" },
        ],
        API_HEALTH_CHANGED: [
          { target: "maintenance", guard: "isEventApiMaintenance", actions: "setApi" },
          { target: "reconnecting", guard: "isEventApiDown", actions: "setApi" },
          { actions: "setApi" },
        ],
        POWERSYNC_STATUS_CHANGED: [
          { target: "reconnecting", guard: "isEventSyncError", actions: "setSync" },
          { actions: "setSync" },
        ],
      },
    },
    reconnecting: {
      entry: "incrementRetry",
      after: {
        BACKOFF_DELAY: { target: "checking" },
      },
      on: {
        RETRY: { target: "manualChecking" },
        NETWORK_CHANGED: [
          { target: "offline", guard: "isEventOffline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
        AUTH_CHANGED: [
          { target: "unauthenticated", guard: "isEventUnauthenticated", actions: "setAuth" },
          { actions: "setAuth" },
        ],
        API_HEALTH_CHANGED: [
          { target: "maintenance", guard: "isEventApiMaintenance", actions: "setApi" },
          { target: "checking", guard: "isEventApiUp", actions: "setApi" },
          { actions: "setApi" },
        ],
        POWERSYNC_STATUS_CHANGED: [
          { target: "checking", guard: "isEventSyncConnected", actions: "setSync" },
          { actions: "setSync" },
        ],
      },
    },
    maintenance: {
      on: {
        RETRY: { target: "manualChecking" },
        API_HEALTH_CHANGED: [
          { target: "checking", guard: "isEventApiUp", actions: "setApi" },
          { actions: "setApi" },
        ],
        NETWORK_CHANGED: [
          { target: "offline", guard: "isEventOffline", actions: "setNetwork" },
          { actions: "setNetwork" },
        ],
      },
    },
    manualChecking: {
      entry: "resetRetry",
      after: {
        800: { target: "checking" },
      },
    },
  },
});

export type ConnectivityStateName =
  | "initializing"
  | "offline"
  | "unauthenticated"
  | "checking"
  | "manualChecking"
  | "connected"
  | "reconnecting"
  | "maintenance";

const allStateNames = new Set<ConnectivityStateName>([
  "initializing",
  "offline",
  "unauthenticated",
  "checking",
  "manualChecking",
  "connected",
  "reconnecting",
  "maintenance",
]);

export function isConnectivityStateName(name: string): name is ConnectivityStateName {
  return (allStateNames as Set<string>).has(name);
}
export type ConnectivityLabelKey =
  | "offline"
  | "connected"
  | "checking"
  | "reconnecting"
  | "maintenance"
  | "unauthenticated";

/**
 * Result of the description mapping.
 */
export interface ConnectivityDescriptionKeys {
  titleKey: string;
  messageKey: string;
  confirmTextKey?: string;
  cancelTextKey?: string;
  hideConfirm?: boolean;
  actionType?: "auth" | "retry";
}
